// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@tps/App/Components/Common/XngZone";
import { XngCustomGroup } from "@tps/XDL/constants";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    tabs = "tabs",
    headline = "headline",
}

interface TabbedContainerProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabProps[];
    };
}

interface TabProps {
    fields: {
        [fields.headline]: string;
    };
}

export interface TabbedContainerStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    tabGroup?: TabGroupPresentationProps;
    tab?: TabPresentationProps;
}

export const tabbedContainerStyles: TabbedContainerStyles = {
    tabGroup: {
        cssOverrides: {
            tabGroup: css`
                li span {
                    padding-bottom: 0;
                }
            `,
        },
    },
};
const styles = tabbedContainerStyles;

const TabbedContainer: React.FC<TabbedContainerProps> = ({ fields, id }) => {
    const { title, tabs } = fields;
    const activeTab = tabs[0].fields.headline;
    return (
        <StyledWrapper {...styles.container} className="tabbed-container">
            <Typography {...styles.title}>{title}</Typography>
            <TabGroup current={activeTab} {...styles.tabGroup}>
                {tabs.map((tab, index) => (
                    <Tab
                        key={`${tab.fields.headline}_${index}`}
                        headline={tab.fields.headline}
                        tabKey={tab.fields.headline}
                        {...styles.tab}
                    >
                        <XngZone contentId={id} zoneName={tab.fields.headline} hint={tab.fields.headline} />
                    </Tab>
                ))}
            </TabGroup>
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    displayName: "Tabbed Container",
    group: XngCustomGroup,
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: translate("Title"),
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.tabs,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields, state) => {
                return item.fields.headline;
            },
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: fields.headline,
                    editorTemplate: "TextField",
                    defaultValue: "",
                    displayName: translate("Tab Name"),
                },
            ],
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: TabbedContainer,
    definition,
};

export default widgetModule;
